// Omnix/client/ui/WorkflowCrud.js
import { ComponentBase } from "../core/ComponentBase.js";
import { VisualWorkflowBuilder } from "../components/VisualWorkflowBuilder.js";
import { WorkflowFormComponent } from "../components/WorkflowFormComponent.js";
import { TagInput } from "../components/TagInput.js";

export class WorkflowCrud extends ComponentBase {
    constructor(config) {
        super(config);
        this.dataCrud = null;
        this.workflowBuilder = null;
        this.currentContextId = null;
        this._boundHandleContextChange = this.handleContextChange.bind(this);
    }

    async initialize() {
        if (this.config.contextChangeEventName) {
            this.appClient.events.local.on(this.config.contextChangeEventName, this._boundHandleContextChange);
        } else {
            this.appClient.events.local.on('projectSelected', this._boundHandleContextChange);
        }

        await this.initializeDataCrud();

        await super.initialize();
    }

    async initAfterLogin() {
        let initialContextData = null;
        if (!this.config.contextChangeEventName) {
            initialContextData = this.appClient.getSharedData('selectedProject');
        }
        if (initialContextData) {
            this.currentContextId = initialContextData._id;
            await this.dataCrud.refreshData();
        } else if (!this.config.contextChangeEventName) {
            this.currentContextId = null;
            await this.dataCrud.refreshData(); // Refresh even with null context for subscription-wide view
        } else {
            this.currentContextId = null;
        }
    }

    async initializeDataCrud() {
        if (this.dataCrud) {
            this.dataCrud.destroy();
            this.dataCrud = null;
        }

        const dataProviderContext = {};
        if (this.currentContextId) {
            dataProviderContext.contextId = this.currentContextId;
        }

        this.dataCrud = await this.createManagedComponent("DataCrud", null, {
            eventGroup: "workflowCrud",
            appClient: this.appClient,
            rpcClass: "WorkflowRpc",
            schemaPath: "collections/workflows",
            title: "Workflows",
            layout: 'horizontal',
            autoLoad: false,
            layoutConfig: {
                formWidth: '600px'
            },
            viewConfig: {
                mode: 'grid',
                fields: [
                    { name: 'name', label: 'Name', render: (value) => `<div class="fw-bold">${value}</div>` },
                    { name: 'description', label: 'Description' },
                    {
                        name: 'enabled', label: 'Enabled', render: (value) => {
                            return value ? '<i class="bi bi-check-circle-fill text-success"></i>' : '<i class="bi bi-x-circle-fill text-danger"></i>';
                        }
                    },

                    {
                        name: 'tags',
                        label: 'Tags',
                        render: (tags) => {
                            if (!tags || tags.length === 0) return '';
                            return tags.map(tag => `<span class="badge bg-secondary me-1">${tag}</span>`).join(' ');
                        }
                    },
                ],
                filter: {
                    enabled: true,
                    fields: {
                        search: {
                            type: 'text',
                            label: 'Search',
                            placeholder: 'Search workflows...'
                        },
                        enabled: {
                            type: 'select',
                            label: 'Status',
                            options: [
                                { value: 'true', label: 'Enabled' },
                                { value: 'false', label: 'Disabled' }
                            ]
                        }
                    }
                },
                actions: [
                    {
                        label: 'Edit Design',
                        icon: 'bi bi-pencil-square',
                        className: 'btn-outline-primary',
                        eventName: 'editDesign'
                    }
                ],
            },
            formConfig: {
                mode: 'edit',
                fields: {
                    name: { type: 'text', label: 'Name' },
                    description: { type: 'text', label: 'Description', multiline: true, rows: 2 },
                    enabled: { type: 'boolean', label: 'Enabled' },
                    category: {
                        type: 'select',
                        label: 'Category',
                        options: [
                            { value: 'user', label: 'User' },
                            { value: 'system', label: 'System' }
                        ],
                    },
                    tags: {
                        type: 'TagInput',
                        label: 'Tags',
                        creatable: true,
                        optionsSourceRpc: 'WorkflowRpc.getWorkflowTags',
                        placeholder: 'Add tags...',
                        component: TagInput,
                    },
                    tools: { type: 'WorkflowFormComponent', label: 'Workflow', component: WorkflowFormComponent },
                    events: { type: 'text', label: 'Trigger Events (comma-separated)' },
                    schema: { type: 'textarea', label: 'Input Schema (JSON)', rows: 5 },
                },
                sections: [
                    {
                        title: "General",
                        fields: ["name", "description", "enabled", "category", "tags", "tools", "events", "schema"],
                    }
                ]
            },
            dataConfig: {
                autoLoad: false,
                pageSize: 25,
                context: dataProviderContext,
            }
        });
        this.setupEventHandlers();
    }

    async handleContextChange(eventData) {
        const newContextId = eventData?.projectData?._id || eventData?._id || null;
        if (this.currentContextId === newContextId) return;

        this.currentContextId = newContextId;
        this.log.info(`Context changed in WorkflowCrud: ${this.currentContextId}`);

        if (this.dataCrud) {
            const newContext = this.currentContextId ? { contextId: this.currentContextId } : {};
            this.dataCrud.setContext(newContext);
            await this.dataCrud.refreshData();
        } else {
            await this.initializeDataCrud();
        }
        if (this.container) this.render();
    }

    setupEventHandlers() {
        if (!this.dataCrud) return;
        this.dataCrud.dataView.on(`editDesign`, (item) => this.handleEditDesign(item));
    }

    async handleEditDesign(item) {
        const wf = item;
        if (!this.workflowBuilder) {
            this.workflowBuilder = await this.createManagedComponent('VisualWorkflowBuilder', document.body, {
                instanceId: `workflowBuilder-${wf._id}`,
                title: wf.name,
            });
        }
        this.workflowBuilder.setData(item.tools || []);
        this.workflowBuilder.show();

        const handleSaved = async (tools) => {
            try {
                wf.tools = tools;
                await this.appClient.rpcClient.call('WorkflowRpc.update', wf);
                this.appClient.notifier.notify("Workflow design updated successfully!", { type: "success" });
                await this.dataCrud.refreshData();
            } catch (error) {
                this.log.error("Error updating workflow design:", error);
                this.appClient.notifier.error(`Error updating workflow design: ${error.message}`);
            }
        }

        const handleClosed = () => {
            this.workflowBuilder.off("workflowSaved", this.workflowSaved);
            this.workflowBuilder.off("workflowClosed", this.workflowClosed);
            this.workflowBuilder.destroy();
            this.workflowBuilder = null;
        }

        this.workflowClosed = handleClosed.bind(this);
        this.workflowSaved = handleSaved.bind(this);

        this.workflowBuilder.on('workflowSaved', this.workflowSaved);
        this.workflowBuilder.on('workflowClosed', this.workflowClosed);
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'workflow-crud-container';
        }
        this.container.innerHTML = '';

        if (this.config.contextChangeEventName && !this.currentContextId) {
            const placeholder = document.createElement('div');
            placeholder.className = 'alert alert-info text-center';
            placeholder.setAttribute('role', 'alert');
            placeholder.innerHTML = `<i class="bi bi-info-circle-fill me-2"></i> Please select a context to manage workflows.`;
            this.container.appendChild(placeholder);
        } else if (this.dataCrud) {
            this.container.appendChild(await this.dataCrud.render());
        } else {
            const loadingPlaceholder = document.createElement('div');
            loadingPlaceholder.textContent = 'Loading workflow manager...';
            this.container.appendChild(loadingPlaceholder);
        }
        return this.container;
    }

    destroy() {
        if (this.config.contextChangeEventName) {
            this.appClient.events.local.off(this.config.contextChangeEventName, this._boundHandleContextChange);
        }

        this.dataCrud?.destroy();
        this.dataCrud = null;
        this.workflowBuilder?.destroy();
        this.workflowBuilder = null;
        super.destroy();
    }
}